import React, { Component, lazy, Suspense } from "react";
import useAkasChartColors from "../../../hooks/useAkasChartColors";
import "./ai-visualizations.css";

const Chart = lazy(() => import("react-apexcharts"));
const kinds = ["sales_trend", "sales_comparison", "top_products"];
const number = (value) => new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(value);
const amount = (value, unit) => unit === "IDR" ? `Rp${number(value)}` : `${number(value)} item`;
const shortNumber = (value) => new Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 1 }).format(value);
const date = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return "";
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });
};

export const validVisualizations = (items) => !Array.isArray(items) ? [] : items.filter((item) =>
  item?.version === 1 && kinds.includes(item.kind) &&
  typeof item.title === "string" && typeof item.scope === "string" &&
  ["IDR", "item"].includes(item.unit) && date(item.start_date) && date(item.end_date) &&
  Array.isArray(item.points) && item.points.length <= 31 &&
  item.points.every((point) => typeof point?.label === "string" && typeof point.value === "number" && Number.isFinite(point.value) && point.value >= 0)
).slice(0, 3);

class ChartBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    return this.state.failed ? <p className="ai-visual-muted">Grafik belum dapat ditampilkan. Angka tetap tersedia di “Lihat data”.</p> : this.props.children;
  }
}

const VisualCard = ({ visual }) => {
  const colors = useAkasChartColors();
  const { points, kind, unit } = visual;
  const total = points.reduce((sum, point) => sum + point.value, 0);
  const comparison = kind === "sales_comparison";
  const headline = comparison ? points[1]?.value || 0 : total;
  const previous = comparison ? points[0]?.value || 0 : 0;
  const change = previous > 0 ? ((headline - previous) / previous) * 100 : null;
  const metric = kind === "top_products" ? "Item dari 5 menu teratas" : comparison ? "Periode terpilih" : "Total penjualan";
  // ApexCharts renders some labels as HTML. Only pass escaped, bounded text to it.
  const chartLabel = (value) => value.slice(0, 55).replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]);
  const categories = points.map((point, index) => comparison ? (index === 0 ? "Sebelumnya" : "Periode ini") : chartLabel(point.label));
  const options = {
    chart: { type: "bar", fontFamily: "inherit", foreColor: colors.moneyOutSoft, toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, background: "transparent" },
    colors: comparison ? [colors.neutral, colors.moneyIn] : [colors.moneyIn],
    plotOptions: { bar: { borderRadius: 4, columnWidth: points.length > 10 ? "65%" : "40%", distributed: comparison } },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: { borderColor: colors.grid, strokeDashArray: 4 },
    xaxis: { categories, axisBorder: { show: false }, axisTicks: { show: false }, labels: { rotate: 0, hideOverlappingLabels: true, trim: true, maxHeight: 44, style: { fontSize: "10px" }, formatter: (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.slice(8, 10) + "/" + value.slice(5, 7) : value }, tooltip: { enabled: false } },
    yaxis: { min: 0, tickAmount: 4, labels: { formatter: shortNumber, style: { fontSize: "10px" } } },
    tooltip: { enabled: false },
    states: { hover: { filter: { type: "none" } }, active: { filter: { type: "none" } } },
  };

  return (
    <section className="ai-visual-card" aria-label={visual.title}>
      <div className="ai-visual-heading">
        <h4>{visual.title}</h4><span className="ai-visual-badge">Data aktual</span>
      </div>
      <p className="ai-visual-muted">{date(visual.start_date)} – {date(visual.end_date)} · {visual.scope}</p>
      <div className="ai-visual-metric"><span>{metric}</span><strong>{amount(headline, unit)}</strong></div>
      {comparison && total > 0 ? <p className="ai-visual-muted">{change === null ? "Persentase belum tersedia karena periode sebelumnya bernilai nol." : `${change > 0 ? "+" : ""}${number(change)}% dibanding periode sebelumnya.`} Periksa rentang kedua periode di bawah.</p> : null}
      {total > 0 ? (
        <div className="ai-visual-chart" aria-label={`Grafik ${visual.title}. Rincian angka tersedia pada Lihat data.`} role="img">
          <ChartBoundary><Suspense fallback={<div className="ai-visual-loading" role="status">Memuat grafik…</div>}>
            <Chart options={options} series={[{ name: unit === "IDR" ? "Total penjualan" : "Item terjual", data: points.map((point) => point.value) }]} type="bar" height={230} width="100%" />
          </Suspense></ChartBoundary>
        </div>
      ) : <div className="ai-visual-empty"><i className="ti ti-chart-bar" aria-hidden="true" />Belum ada penjualan pada periode ini.</div>}
      <details className="ai-visual-details">
        <summary>Lihat data</summary>
        <div className="table-responsive">
          <table className="table table-sm table-vcenter mb-0">
            <thead><tr><th>{kind === "top_products" ? "Produk" : "Periode"}</th><th className="text-end">{unit === "IDR" ? "Total penjualan" : "Item terjual"}</th></tr></thead>
            <tbody>{points.map((point, index) => <tr key={index}><td>{point.label}</td><td className="text-end">{amount(point.value, unit)}</td></tr>)}</tbody>
            {!comparison && points.length > 0 ? <tfoot><tr className="bg-primary-lt"><th>Total</th><th className="text-end">{amount(total, unit)}</th></tr></tfoot> : null}
          </table>
        </div>
      </details>
      <p className="ai-visual-note">{typeof visual.note === "string" ? visual.note : ""} Data saat jawaban dibuat.</p>
    </section>
  );
};

const AiVisualizations = ({ items }) => <>{validVisualizations(items).map((visual, index) => <VisualCard key={`${visual.kind}-${index}`} visual={visual} />)}</>;
export default AiVisualizations;
